/**
 * SeriesPreview Component
 *
 * A compact inline preview of a temporal series, showing key values
 * and a mini sparkline-style visualization. Used in "Used By" sections
 * to collapse multiple series nodes into one meaningful display.
 */

import React from 'react';
import { useGraph } from '../GraphContext';
import { ComputedNode } from '../../types';
import { temporalId, getSeriesPatternDescription } from '../../temporal';

export interface SeriesPreviewProps {
  seriesId: string;
  seriesLabel: string;
  pattern: string;
  totalYears: number;
  onYearClick?: (nodeId: string) => void;
}

export function SeriesPreview({
  seriesId,
  seriesLabel,
  pattern,
  totalYears,
  onYearClick,
}: SeriesPreviewProps): React.ReactElement {
  const ctx = useGraph();

  // Get values for all years
  const yearValues: number[] = [];
  for (let year = 0; year < totalYears; year++) {
    const nodeId = temporalId(seriesId, year);
    const value = ctx.getValue(nodeId);
    yearValues.push(value ?? 0);
  }

  // Get unit from first node
  const firstNode = ctx.getNode(temporalId(seriesId, 0)) as ComputedNode | undefined;
  const unit = firstNode?.unit || 'number';

  // Calculate min/max for sparkline
  const minVal = Math.min(...yearValues);
  const maxVal = Math.max(...yearValues);
  const range = maxVal - minVal || 1;

  // Key values to display
  const year0 = yearValues[0];
  const yearFinal = yearValues[totalYears - 1];
  const growth = year0 !== 0 ? ((yearFinal / year0) - 1) * 100 : 0;

  return (
    <div className="border border-gray-200 bg-gray-50 p-3">
      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="font-medium text-gray-900 text-sm">{seriesLabel}</div>
          <div className="text-xs text-gray-500">
            {totalYears} years · {getSeriesPatternDescription(pattern as any)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Year 0 → {totalYears - 1}</div>
          <div className="text-sm font-medium text-gray-900">
            {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Mini sparkline */}
      <div className="h-8 flex items-end gap-px mb-2">
        {yearValues.map((val, idx) => {
          const height = ((val - minVal) / range) * 100;
          const isFirst = idx === 0;
          const isLast = idx === totalYears - 1;
          return (
            <button
              key={idx}
              onClick={() => onYearClick?.(temporalId(seriesId, idx))}
              className={`
                flex-1 min-w-0 transition-colors
                ${isFirst || isLast ? 'bg-gray-600' : 'bg-gray-300 hover:bg-gray-400'}
              `}
              style={{ height: `${Math.max(4, height)}%` }}
              title={`Year ${idx}: ${ctx.formatValue(val, unit)}`}
            />
          );
        })}
      </div>

      {/* Key values row */}
      <div className="flex justify-between text-xs">
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, 0))}
          className="text-left hover:text-gray-900"
        >
          <span className="text-gray-500">Yr 0:</span>{' '}
          <span className="text-gray-700">{ctx.formatValue(year0, unit)}</span>
        </button>
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, Math.floor(totalYears / 2)))}
          className="text-center hover:text-gray-900"
        >
          <span className="text-gray-500">Yr {Math.floor(totalYears / 2)}:</span>{' '}
          <span className="text-gray-700">{ctx.formatValue(yearValues[Math.floor(totalYears / 2)], unit)}</span>
        </button>
        <button
          onClick={() => onYearClick?.(temporalId(seriesId, totalYears - 1))}
          className="text-right hover:text-gray-900"
        >
          <span className="text-gray-500">Yr {totalYears - 1}:</span>{' '}
          <span className="text-gray-700">{ctx.formatValue(yearFinal, unit)}</span>
        </button>
      </div>
    </div>
  );
}

export default SeriesPreview;
